/**
 * Quote-accept → lead conversion — the ORCHESTRATION (2026-07-03,
 * leads/quotes streamline Phase 1d; hardened per Vex gate F1/F2 same day;
 * F1 semantics updated same day for the owner-approved Option A rules
 * change — see below).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  THE CIRCLE THIS BREAKS (audit §3.4, queued in AVBP_LEADQUOTE_STATE.md)
 * ─────────────────────────────────────────────────────────────────────────
 *  A lead-origin quote only received a `projectId` AFTER conversion, but
 *  `acceptQuote` refused without a `projectId`, while the lead's Convert
 *  gate wanted the agreed price that acceptance would produce. Inverted.
 *
 *  THE SEQUENCE (fail-fast preconditions FIRST — F1, Option A):
 *    0. PRECONDITIONS before any write:
 *       · the caller's session must be Sales/Admin (mirrors the deployed
 *         rules' actor set — the new gate (3), functions branch
 *         `feat/quote-manual-accept-rule` @ 7e9aa16, allows sales/admin to
 *         stamp sent→accepted regardless of the stored `acceptanceMode`,
 *         provided the write carries the accept SHAPE: post-write
 *         `acceptanceMode=='manual'`, non-empty `projectId`, non-empty
 *         `updatedBy`, a picklist reason);
 *       · a FRESH read of the quote doc must show `status=='sent'`
 *         (never trust the UI copy — CFs re-stamp server-side);
 *       · the lead must exist and (for a fresh mint) pass the 6 must-haves.
 *       A failed precondition refuses in plain language with ZERO writes.
 *    1. CLAIM the conversion in a Firestore TRANSACTION (F2):
 *       `claimLeadConversionTx` stamps a RUN-scoped claim id (quote id +
 *       nonce — two tabs accepting the SAME quote still serialise) on the
 *       lead via the whitelisted path. A concurrent run loses the
 *       transaction → 'busy'. A busy loser CONVERGES onto an already-minted
 *       project when one is findable; otherwise it refuses. At most one
 *       live claim mints.
 *    2. RESUME CHECK: look for a project a previous (crashed) run already
 *       minted for this lead (`projects.sourceLeadId`, stamped in the same
 *       create as the project doc). Found → re-attach the lead
 *       (resumeLeadConversion) and SKIP minting.
 *    3. Otherwise price the lead FROM the quote (pure `priceLeadFromQuote`;
 *       the accepted net grand total IS the agreed price — one money spine),
 *       and convert via the EXISTING promote path (`convertLeadPersist` →
 *       `convertLeadDirect` → `convertLeadToProject`; the lead reaches
 *       `converted` through `transitionLead` inside that bridge). The
 *       accepting quote is excluded from the open-sent-quote guard so it
 *       never blocks its own conversion.
 *    4. Persist the converted lead (claim cleared in the same snapshot).
 *    5. Seed the agreed price on the project through the canonical
 *       `setProjectAgreedPrice` (from `acceptedAgreedPrice(quote)`).
 *    6. Stamp the quote sent→accepted in the rule-required SHAPE
 *       (`acceptanceMode:'manual'` + non-empty `projectId` + `updatedBy`).
 *       DEFENSIVE: a rules denial here (rule drift or a missing team claim —
 *       the accept rule itself is live) is caught and surfaced honestly — the project/price are in
 *       place and a RETRY of Accept recovers via the resume path; it never
 *       silently half-fails.
 *
 *  PARTIAL-FAILURE MAP (durable-claim-first — the honest version, F2):
 *    · fail at 0/1 → nothing written (0 is read-only; the claim tx is
 *      atomic). Retry re-runs from scratch.
 *    · fail at 3 (mint) → only the CLAIM is durable. It is released
 *      best-effort; an unreleased claim expires after CONVERT_CLAIM_TTL_MS.
 *      If the mint half-completed (project doc written, rest crashed), the
 *      next run's RESUME/CONVERGE check finds it via `sourceLeadId` — one
 *      project, never two.
 *    · fail at 4 → the project exists AND is findable (sourceLeadId); we
 *      CONTINUE (5→6) and return a warning telling staff the lead record
 *      needs a re-save. A retry — even after 4 AND 6 both failed — hits the
 *      live claim, finds the minted project, CONVERGES onto it, heals the
 *      lead and stamps the quote. Still exactly one project.
 *    · fail at 5/6 → retry converges idempotently (`setProjectAgreedPrice`
 *      no-ops on an unchanged value; the stamp is the same write).
 *    · RESIDUAL WINDOW (stated honestly): a run frozen for longer than the
 *      claim TTL (5 min) between winning the claim and writing the project
 *      doc, waking up EXACTLY while a second run (which legitimately took
 *      over the stale claim) is minting — both could mint. Closing it fully
 *      needs a server-side cross-collection transaction; client-side the
 *      TTL + resume-check reduces it to a frozen-tab-only corner.
 *
 *  PURE + SDK-free: every impure seam is INJECTED (`AcceptConvertDeps`), so
 *  the Node test runner exercises the real ordering/interleaving/recovery
 *  with in-memory deps and quoteStore wires the Firestore ones. The deployed
 *  CF `onQuoteAcceptManual_promoteLeadToClient` (legacy Phase-1 promote)
 *  short-circuits when the accept write already carries a `clientId` —
 *  which this flow guarantees — so no legacy avbpClientId client is minted
 *  alongside the portal C##### one.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  releaseConversionClaim,
  resumeLeadConversion,
  type Lead,
} from './leads';
import {
  applyLeadPrice,
  completionMessage,
  missingMustHaves,
} from './leadQuote';
import { acceptedAgreedPrice, type Quote } from './quoteModel';
import type { Currency } from './firms';
// Type-only (erased at compile) — no runtime dependency on the impure store.
import type { ConvertClientTarget } from './projectStore';

/* ════════════════════ Pure: price the lead from the quote ════════════════════ */

/**
 * PURE — write the ACCEPTED quote's net grand total onto the lead as the
 * agreed price (the one figure the money gate expects), via the existing
 * `applyLeadPrice` mutator so firm routing / GST behave exactly like the
 * Set-price door. A zero-total quote (fully credited/waived) becomes an
 * explicit No-charge engagement so the convert bridge accepts it.
 */
export function priceLeadFromQuote(lead: Lead, quote: Quote, now: string): Lead {
  const agreed = acceptedAgreedPrice(quote);
  const noCharge = agreed.value <= 0;
  return applyLeadPrice(
    lead,
    {
      tier: quote.tier,
      currency: agreed.currency,
      value: noCharge ? null : agreed.value,
      gst: agreed.gst,
      firmId: quote.firmId,
      noCharge,
      noChargeReason: noCharge ? `Accepted quote ${quote.id} at zero total` : '',
    },
    now,
  );
}

/* ════════════════════ Injected seams ════════════════════ */

/** The precondition fields of the FRESH quote doc (F1). */
export interface FreshQuoteGate {
  status: string;
  /** Kept for observability; the Option A rule no longer gates on it. */
  acceptanceMode: string | null;
}

/** What the convert seam returns (mirrors projectStore.ConvertPersistResult). */
export interface AcceptConvertOutcome {
  lead: Lead;
  projectId: string;
  clientNo: string;
  clientId: string;
  newClientCreated: boolean;
}

/** Outcome of the conversion-claim transaction (mirrors leadStore). */
export type ClaimOutcome =
  | { kind: 'claimed'; lead: Lead }
  | { kind: 'converted'; lead: Lead }
  | { kind: 'busy' }
  | { kind: 'missing' };

/** The caller's role gate — mirrors the deployed rules' isSalesOrAdmin. */
export interface AcceptRoleGate {
  isAdmin: boolean;
  isSales: boolean;
}

/** The impure seams the orchestrator needs — quoteStore wires Firestore ones. */
export interface AcceptConvertDeps {
  /** Load the quote's lead (pre-claim fast-path checks only). */
  getLead(id: string): Promise<Lead | null>;
  /** F1 — FRESH-read the quote doc's precondition fields (never the UI copy). */
  getQuoteFresh(quoteId: string): Promise<FreshQuoteGate | null>;
  /** F2 — the atomic conversion claim (leadStore.claimLeadConversionTx). */
  claimConversion(
    leadId: string,
    claimId: string,
    actorName: string,
  ): Promise<ClaimOutcome>;
  /** F2 — a project a previous run already minted for this lead, if any. */
  findConvertedProject(
    leadId: string,
  ): Promise<{ projectId: string; clientNo: string; clientId: string | null } | null>;
  /** Resolve where the conversion lands (existing contact vs new client). */
  resolveConvertTarget(lead: Lead): Promise<ConvertClientTarget>;
  /**
   * THE EXISTING promote path (convertLeadPersist). `excludeQuoteId` must be
   * threaded to the open-sent-quote guard so the accepting quote never
   * blocks its own conversion.
   */
  convertLead(
    lead: Lead,
    actorName: string,
    nowISO: string,
    target: ConvertClientTarget,
    excludeQuoteId: string,
  ): Promise<AcceptConvertOutcome>;
  /** Persist a lead through the leadStore whitelist path. */
  persistLead(lead: Lead, actorName: string, activityWhat: string): Promise<void>;
  /** The canonical agreed-price seed (setProjectAgreedPrice). CALL-only. */
  setAgreedPrice(
    projectId: string,
    input: {
      tier: string;
      currency: Currency;
      value: number;
      prior: { currency: Currency | null; value: number | null };
      gst?: boolean;
      note?: string;
    },
    actorName: string,
  ): Promise<unknown>;
  /**
   * The rule-conformant sent→accepted stamp, in the accept SHAPE the new
   * gate (3) requires: post-write `acceptanceMode:'manual'`, NON-EMPTY
   * `projectId`, non-empty `updatedBy` (+ the picklist reason). `acceptedAt`
   * stays CF-stamped — never written client-side.
   */
  stampQuoteAccepted(
    quote: Quote,
    stamp: { projectId: string; clientId: string | null; acceptanceReason: string },
    actorName: string,
  ): Promise<void>;
}

/* ════════════════════ The orchestrator ════════════════════ */

export interface AcceptDriveResult {
  ok: boolean;
  /**
   * 'not_sent' | 'not_permitted' (caller is not Sales/Admin — F1) |
   * 'no_project' (no lead AND no project) | 'quote_not_found' |
   * 'lead_not_found' | 'incomplete' (must-haves missing — see message) |
   * 'convert_in_progress' (another run holds the claim, nothing minted yet —
   * F2) | 'convert_failed' | 'stamp_denied' (the final quote stamp was
   * blocked by security rules; project + price ARE in place, retry recovers)
   * — undefined when ok.
   */
  error?: string;
  /** Plain-language detail for the staff toast. */
  message?: string;
  /** The engagement the accepted price landed on (set when ok / stamp_denied). */
  projectId?: string;
  /** Non-fatal warnings (e.g. the lead re-save failed and needs attention). */
  warning?: string;
}

/**
 * F1 — validate the FRESH quote doc's status precondition. Returns null when
 * acceptable, else the refusal. Exported so the project-origin accept path
 * applies the identical precondition. (Option A: the stored `acceptanceMode`
 * no longer refuses — the deployed gate (3) allows sales/admin manual accept
 * regardless of mode, keyed on the write SHAPE instead.)
 */
export function freshQuoteRefusal(
  fresh: FreshQuoteGate | null,
): AcceptDriveResult | null {
  if (!fresh) {
    return {
      ok: false,
      error: 'quote_not_found',
      message: 'This quote no longer exists — refresh the list.',
    };
  }
  if (fresh.status !== 'sent') {
    return {
      ok: false,
      error: 'not_sent',
      message:
        fresh.status === 'accepted'
          ? 'This quote is already accepted.'
          : 'Send the quote to the client first.',
    };
  }
  return null;
}

/**
 * F1 — the caller-session role precondition (mirrors the deployed rules'
 * isSalesOrAdmin actor set so a non-sales session is refused BEFORE any
 * write instead of at the rules-denied final stamp). Exported for the
 * project-origin path. `undefined` role (a caller that cannot know) defers
 * to the rules backstop.
 */
export function roleRefusal(
  role: AcceptRoleGate | undefined,
): AcceptDriveResult | null {
  if (role && !role.isAdmin && !role.isSales) {
    return {
      ok: false,
      error: 'not_permitted',
      message: 'Only Sales or Admin can accept a quote. Nothing was changed.',
    };
  }
  return null;
}

/**
 * Accept a LEAD-ORIGIN quote (no `projectId` yet): acceptance DRIVES the
 * conversion. See the module header for the sequence + failure map.
 */
export async function acceptLeadOriginQuote(
  quote: Quote,
  prior: { currency: Currency | null; value: number | null },
  actorName: string,
  acceptanceReason: string,
  deps: AcceptConvertDeps,
  nowISO: string,
  role?: AcceptRoleGate,
): Promise<AcceptDriveResult> {
  // ── Step 0: fail-fast preconditions, ZERO writes (F1) ──
  const denied = roleRefusal(role);
  if (denied) return denied;
  if (quote.status !== 'sent') {
    return { ok: false, error: 'not_sent', message: 'Send the quote to the client first.' };
  }
  const refusal = freshQuoteRefusal(await deps.getQuoteFresh(quote.id));
  if (refusal) return refusal;

  if (!quote.leadId) {
    // No lead AND no project — the accepted money has nowhere to land.
    return { ok: false, error: 'no_project', message: 'This quote has no lead and no project.' };
  }
  const lead0 = await deps.getLead(quote.leadId);
  if (!lead0) {
    return {
      ok: false,
      error: 'lead_not_found',
      message: `Lead ${quote.leadId} on this quote no longer exists.`,
    };
  }

  let projectId: string;
  let clientId: string | null;
  let warning: string | undefined;

  if (lead0.convertedProjectId) {
    // Already converted (a follow-on quote on the same lead) — NO second
    // conversion. Re-use the engagement; the accepted total re-seeds the
    // same one agreed price below.
    projectId = lead0.convertedProjectId;
    clientId = lead0.contactId ?? quote.clientId ?? null;
  } else {
    // Zero-write pre-gate: the 6 must-haves, checked BEFORE the claim so an
    // obviously-incomplete lead is refused without touching Firestore.
    const preGateMiss = missingMustHaves(priceLeadFromQuote(lead0, quote, nowISO));
    if (preGateMiss.length > 0) {
      return {
        ok: false,
        error: 'incomplete',
        message:
          completionMessage(priceLeadFromQuote(lead0, quote, nowISO)) ??
          'Complete the lead record (name, WhatsApp, email, service, GST answer) to accept.',
      };
    }

    // F2 step 1 — the DURABLE claim. Exactly one concurrent run passes. The
    // claim id is RUN-scoped (quote id + nonce), never just the quote id —
    // two tabs accepting the SAME quote must still serialise.
    const claimId = `${quote.id}:${Math.random().toString(36).slice(2, 10)}`;
    const claim = await deps.claimConversion(quote.leadId, claimId, actorName);
    if (claim.kind === 'missing') {
      return {
        ok: false,
        error: 'lead_not_found',
        message: `Lead ${quote.leadId} on this quote no longer exists.`,
      };
    }
    if (claim.kind === 'busy') {
      // Another run holds a LIVE claim. If it already minted the project
      // (durable + findable), CONVERGE onto it — same project, same values,
      // idempotent writes — so a retry after a 4-then-6 partial failure
      // recovers immediately. If nothing was minted yet, refuse: the claim
      // holder is mid-mint and must not be raced.
      const minted = await deps.findConvertedProject(quote.leadId);
      if (!minted) {
        return {
          ok: false,
          error: 'convert_in_progress',
          message:
            'Another accept/convert for this lead is already in progress ' +
            '(a second tab or a racing retry). Wait a moment and refresh — ' +
            'nothing was changed here.',
        };
      }
      projectId = minted.projectId;
      clientId = minted.clientId ?? lead0.contactId ?? null;
      try {
        await deps.persistLead(
          resumeLeadConversion(
            priceLeadFromQuote(lead0, quote, nowISO),
            minted.projectId,
            minted.clientNo,
            nowISO,
          ),
          actorName,
          `Conversion resumed → ${minted.projectId} (quote ${quote.id})`,
        );
      } catch (e) {
        warning =
          `The lead record could not be re-saved (${(e as Error).message}) — ` +
          `project ${minted.projectId} was recovered; re-save the lead.`;
      }
    } else if (claim.kind === 'converted') {
      const pid =
        claim.lead.convertedProjectId ??
        (await deps.findConvertedProject(claim.lead.id))?.projectId ??
        null;
      if (!pid) {
        return {
          ok: false,
          error: 'convert_failed',
          message:
            'This lead is marked converted but its project could not be found — ' +
            'open the lead record and fix the project link first.',
        };
      }
      projectId = pid;
      clientId = claim.lead.contactId ?? quote.clientId ?? null;
    } else {
      // F2 step 2 — RESUME CHECK: a previous (crashed) run may already have
      // minted the project. Found → re-attach, never mint a second one.
      const existing = await deps.findConvertedProject(claim.lead.id);
      if (existing) {
        projectId = existing.projectId;
        clientId = existing.clientId ?? claim.lead.contactId ?? null;
        try {
          await deps.persistLead(
            resumeLeadConversion(
              priceLeadFromQuote(claim.lead, quote, nowISO),
              existing.projectId,
              existing.clientNo,
              nowISO,
            ),
            actorName,
            `Conversion resumed → ${existing.projectId} (quote ${quote.id})`,
          );
        } catch (e) {
          warning =
            `The lead record could not be re-saved (${(e as Error).message}) — ` +
            `project ${existing.projectId} was recovered; re-save the lead.`;
        }
      } else {
        // Steps 3–4 — fresh mint through the EXISTING promote path.
        const priced = priceLeadFromQuote(claim.lead, quote, nowISO);
        // Re-gate on the FRESH claimed lead (it may differ from the UI copy).
        if (missingMustHaves(priced).length > 0) {
          await releaseClaimBestEffort(deps, claim.lead, actorName, nowISO);
          return {
            ok: false,
            error: 'incomplete',
            message:
              completionMessage(priced) ??
              'Complete the lead record (name, WhatsApp, email, service, GST answer) to accept.',
          };
        }
        const target = await deps.resolveConvertTarget(priced);
        let converted: AcceptConvertOutcome;
        try {
          converted = await deps.convertLead(priced, actorName, nowISO, target, quote.id);
        } catch (e) {
          await releaseClaimBestEffort(deps, claim.lead, actorName, nowISO);
          return {
            ok: false,
            error: 'convert_failed',
            message: `Could not convert the lead: ${(e as Error).message}`,
          };
        }
        projectId = converted.projectId;
        clientId = converted.clientId ?? null;
        // Persist the converted lead — the claim is cleared in the same
        // snapshot. Monotonic progress: a failure here does NOT abort (the
        // project exists AND is findable via sourceLeadId, so a retry of
        // Accept converges onto it); it is surfaced as a warning.
        try {
          await deps.persistLead(
            releaseConversionClaim(converted.lead, nowISO),
            actorName,
            `Converted → ${projectId} via accepted quote ${quote.id}`,
          );
        } catch (e) {
          warning =
            `The lead record could not be re-saved (${(e as Error).message}) — ` +
            `the project ${projectId} was created; re-save the lead ` +
            `(a retry of Accept recovers it automatically).`;
        }
      }
    }
  }

  return seedAndStamp(deps, quote, prior, actorName, acceptanceReason, projectId, clientId, warning);
}

/**
 * Steps 5–6 — seed the agreed price through the ONE canonical spine (the
 * quote's net grand total via setProjectAgreedPrice), then stamp the quote
 * sent→accepted in the rule-required shape. A rules denial at the stamp is
 * caught and surfaced honestly (`stamp_denied`) — the project and price are
 * in place and a retry of Accept converges/recovers.
 */
async function seedAndStamp(
  deps: AcceptConvertDeps,
  quote: Quote,
  prior: { currency: Currency | null; value: number | null },
  actorName: string,
  acceptanceReason: string,
  projectId: string,
  clientId: string | null,
  warning: string | undefined,
): Promise<AcceptDriveResult> {
  const agreed = acceptedAgreedPrice(quote);
  await deps.setAgreedPrice(
    projectId,
    {
      tier: agreed.tier,
      currency: agreed.currency,
      value: agreed.value,
      prior,
      gst: agreed.gst,
      note: `Accepted quote ${quote.id}`,
    },
    actorName,
  );
  try {
    await deps.stampQuoteAccepted(
      quote,
      { projectId, clientId, acceptanceReason },
      actorName,
    );
  } catch (e) {
    return {
      ok: false,
      error: 'stamp_denied',
      projectId,
      message:
        `The client and project (${projectId}) were created and the agreed ` +
        `price is seeded, but the quote could not be marked accepted — ` +
        `security rules blocked the write (${(e as Error).message}). ` +
        `Tell Larry (likely a missing team claim or rule drift); ` +
        `retrying Accept later finishes the job without duplicating anything.`,
    };
  }
  return { ok: true, projectId, ...(warning ? { warning } : {}) };
}

/** Release a claim after a refusal/failure — best-effort (TTL is the net). */
async function releaseClaimBestEffort(
  deps: AcceptConvertDeps,
  claimedLead: Lead,
  actorName: string,
  nowISO: string,
): Promise<void> {
  try {
    await deps.persistLead(
      releaseConversionClaim(claimedLead, nowISO),
      actorName,
      'Conversion claim released (accept refused)',
    );
  } catch {
    /* the TTL expires the claim */
  }
}
